import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Pagination from "../../../components/BaseUI/Pagination";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import {
  FiArrowLeft,
  FiCheck,
  FiEdit2,
  FiList,
  FiServer,
  FiTrash2,
  FiClock,
  FiLink,
} from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import {
  partBaseLabel,
  partInputLabel,
  partDocById,
  getOutputSettings,
  inputMainConfigured,
  resolveEntryAllowedPartIds,
  formatEntryConsumptionSummary,
  getOutputAssignmentLabels,
  collectAssignmentValidationIssues,
  collectVariantSettingsIssues,
  isVariantSettingsComplete,
  recordsToMultiPartAssignments,
  inputMainPartFromCatalog,
  computeOutputEquivalentWeight,
  quantityWithUnit,
  partUnitLabelForDoc,
  inferEquivalentUnitSymbol,
  getMetricDisplayParts,
} from "./partMachinesUtils";
import "./PartMachines.scss";

const MetricAmount = ({ value, unit, className }) => {
  const { value: shown, unit: u } = getMetricDisplayParts(value, unit);
  return (
    <span className={className}>
      <span className="assign-metric__value">{shown}</span>
      {u ? <span className="assign-metric__unit">{u}</span> : null}
    </span>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";
const PER_PAGE = 10;

const paginate = (items, page) => {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * PER_PAGE;
  return {
    total,
    totalPages,
    page: safePage,
    start,
    items: items.slice(start, start + PER_PAGE),
  };
};

const tableWrapClass = (total) =>
  `pm-view__table-wrap${total >= PER_PAGE ? " pm-view__table-wrap--scroll" : ""}`;

const assignListClass = (count) =>
  `pm-view__assign-list mb-0${count >= PER_PAGE ? " pm-view__assign-list--scroll" : ""}`;

const ViewTablePagination = ({ total, page, onPageChange }) => {
  if (total < PER_PAGE) return null;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  return (
    <div className="pm-view__pagination">
      <Pagination
        totalItems={total}
        totalPerPage={PER_PAGE}
        currentPage={safePage}
        options={{
          selectQuantity: false,
          simpleCounting: true,
          skipPrevious: true,
          skipNext: true,
          previous: true,
          next: true,
        }}
        onPageChange={({ pagination }) =>
          onPageChange(pagination?.currentPage || 1)
        }
      />
    </div>
  );
};

const ViewTablePanel = ({ total, page, onPageChange, children }) => (
  <div className="pm-view__table-panel">
    <div className={tableWrapClass(total)}>{children}</div>
    <ViewTablePagination total={total} page={page} onPageChange={onPageChange} />
  </div>
);

const entryInputSummary = (entry, idx, mainPartById, catalog) => {
  const inMain = entry.inputMainPartId
    ? mainPartById(entry.inputMainPartId)
    : null;
  if (!inMain) {
    return {
      title: `Girdi ${idx + 1}`,
      meta: "Girdi tanımlayın",
    };
  }
  const mp = mainPartById(entry.inputMainPartId);
  const mainVariantCount = Number(mp?.variantCount) || 0;
  const allowedCount = resolveEntryAllowedPartIds(
    entry,
    catalog,
    mainPartById,
  ).length;
  const countLabel =
    mainVariantCount > 0 ? `${allowedCount} varyant` : "1 parça";
  const ratioMeta = formatEntryConsumptionSummary(
    entry,
    catalog,
    mainPartById,
  );
  return {
    title: partBaseLabel(inMain),
    meta: `${countLabel} · ${ratioMeta}`,
  };
};

const PartMachineAssignmentView = () => {
  const { machineId } = useParams();
  const navigate = useNavigate();
  useTitle("Rovion MES - Makine Ataması Detay");

  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/part-machines",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mainParts, setMainParts] = useState([]);
  const [inputParts, setInputParts] = useState([]);
  const [models, setModels] = useState([]);
  const [machineDoc, setMachineDoc] = useState(null);
  const [parts, setParts] = useState([]);
  const [activePartIdx, setActivePartIdx] = useState(0);
  const [inputsPage, setInputsPage] = useState(1);
  const [outputsPage, setOutputsPage] = useState(1);
  const [settingsPage, setSettingsPage] = useState(1);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const dismissToast = useCallback(
    (id) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    [],
  );
  const addToast = useCallback((message, type = "success") => {
    setToasts((prev) => [...prev, { id: Date.now(), message, type }]);
  }, []);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const partsCatalog = useMemo(() => {
    const byId = new Map();
    for (const p of [...mainParts, ...inputParts]) {
      const id = String(p?._id || "");
      if (id) byId.set(id, p);
    }
    return [...byId.values()];
  }, [mainParts, inputParts]);

  const mainPartById = (id) => {
    const idStr = String(id || "");
    const fromOutput = mainParts.find((p) => String(p._id) === idStr);
    if (fromOutput) return fromOutput;
    return inputMainPartFromCatalog(idStr, partsCatalog);
  };

  useEffect(() => {
    const load = async () => {
      if (!machineId) return;
      setLoading(true);
      try {
        const [mainRes, inputRes, modelsRes, byMachineRes] = await Promise.all([
          axios.get(`${API_URL}/api/part-machines/main-parts`, authHeaders()),
          axios.get(
            `${API_URL}/api/part-machines/parts-for-input`,
            authHeaders(),
          ),
          axios.get(`${API_URL}/api/device-models`, authHeaders()),
          axios.get(
            `${API_URL}/api/part-machines/by-machine/${machineId}`,
            authHeaders(),
          ),
        ]);
        const loadedMain = mainRes.data.mainParts || [];
        const loadedInput = inputRes.data.parts || [];
        const md = modelsRes.data;
        setModels(Array.isArray(md) ? md : md.models || []);
        setMainParts(loadedMain);
        setInputParts(loadedInput);

        const records = byMachineRes.data.partMachines || [];
        const catalog = [...loadedMain, ...loadedInput];
        setParts(recordsToMultiPartAssignments(records, catalog));
        setActivePartIdx(0);
        setInputsPage(1);
        setOutputsPage(1);
        setSettingsPage(1);
        setMachineDoc(records[0]?.machine || byMachineRes.data.machine || null);
      } catch (err) {
        addToast(err?.response?.data?.message || "Kayıt yüklenemedi", "danger");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [machineId, authHeaders, addToast]);

  const modelLabelById = (id) => {
    if (!id) return "—";
    const m = models.find((x) => String(x._id) === String(id));
    if (!m) return String(id);
    return `${m.code || "—"} — ${m.name || "—"}`;
  };

  const handleDelete = async () => {
    try {
      await axios.put(
        `${API_URL}/api/part-machines/by-machine/${machineId}`,
        { parts: [] },
        authHeaders(),
      );
      addToast("Makine ataması silindi", "success");
      navigate("/part-machines");
    } catch (err) {
      addToast(err?.response?.data?.message || "Silme başarısız", "danger");
    } finally {
      setConfirmDelete(false);
    }
  };

  const breadcrumbs = [
    { label: "Parça / Makine", url: "/part-machines" },
    { label: "Detay" },
  ];

  const machineLabel = machineDoc
    ? `${machineDoc.code || "—"} — ${machineDoc.name || "—"}`
    : "—";

  const activePart = parts[activePartIdx] || null;

  useEffect(() => {
    setInputsPage(1);
    setOutputsPage(1);
    setSettingsPage(1);
  }, [activePartIdx]);

  return (
    <AppPage
      title="Makine Ataması Detay"
      breadcrumbs={breadcrumbs}
      className="part-machine-assign part-machine-view"
      actions={
        <div className="d-flex gap-2">
          <Button
            appearance="danger-subtle"
            icon={<FiArrowLeft />}
            onClick={() => navigate("/part-machines")}
          >
            Listeye dön
          </Button>

          {canWrite && (
            <div className="d-flex gap-2">
              <Button
                appearance="primary-animated"
                icon={<FiEdit2 />}
                onClick={() => navigate(`/part-machines/assign/${machineId}`)}
              >
                Düzenle
              </Button>
              <Button
                appearance="danger-subtle"
                icon={<FiTrash2 />}
                onClick={() => setConfirmDelete(true)}
              >
                Sil
              </Button>
            </div>
          )}
        </div>
      }
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type === "error" ? "danger" : t.type || "success"}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        width="small"
      >
        <ModalHeader>Makine Atamasını Sil</ModalHeader>
        <ModalBody>
          <p className="sub-title-page">
            <strong>{machineLabel}</strong> için tüm parça atamaları silinsin
            mi?
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button appearance="danger" onClick={handleDelete}>
              Sil
            </Button>
            <Button appearance="subtle" onClick={() => setConfirmDelete(false)}>
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <CardContainer className="assign-page-body">
        <div className="card border-0 assign-wizard-card">
          {loading ? (
            <p className="text-muted mb-0">Yükleniyor…</p>
          ) : (
            <div className="assign-section pm-view">
              <div className="pm-view__topbar">
                <div></div>
                <div className="pm-view__machine">
                  <FiServer size={18} />
                  <div className="min-w-0">
                    <div className="pm-view__machine-label">Makine</div>
                    <div className="pm-view__machine-value text-truncate">
                      {machineLabel}
                    </div>
                  </div>
                </div>
                <div className="pm-view__stats">
                  <Lozenge appearance="default-subtle" isBold>
                    {parts.length} parça
                  </Lozenge>
                </div>
              </div>

              <div className="part-tabs-bar pm-view__tabs">
                {parts.map((part, idx) => {
                  const outMain = mainPartById(part.output?.baseKey);
                  const tabLabel = outMain
                    ? outMain.code || outMain.name || `Parça ${idx + 1}`
                    : `Parça ${idx + 1}`;
                  const isActive = activePartIdx === idx;
                  const matchIssues = collectAssignmentValidationIssues(
                    part.output,
                    part.inputs,
                    partsCatalog,
                    mainPartById,
                  );
                  const variantIssues = collectVariantSettingsIssues(
                    part.output,
                    partsCatalog,
                  );
                  const isDone =
                    !!part.output?.baseKey &&
                    matchIssues.length === 0 &&
                    variantIssues.length === 0;
                  return (
                    <div
                      key={part.id}
                      className={`part-tab${isActive ? " part-tab--active" : ""}${isDone ? " part-tab--done" : ""}`}
                    >
                      <button
                        type="button"
                        className="part-tab__label"
                        onClick={() => setActivePartIdx(idx)}
                      >
                        {isDone && !isActive ? (
                          <FiCheck size={11} strokeWidth={3} className="me-1" />
                        ) : null}
                        {tabLabel}
                      </button>
                    </div>
                  );
                })}
              </div>

              {!activePart ? (
                <p className="text-muted mb-0">Kayıt bulunamadı.</p>
              ) : (
                (() => {
                  const out = activePart.output;
                  const outMain = mainPartById(out.baseKey);
                  const title = outMain ? partBaseLabel(outMain) : "—";
                  const configuredInputs =
                    activePart.inputs.filter(inputMainConfigured);
                  const selectedVariants = (out.selectedPartIds || []).map(
                    String,
                  );
                  const matchIssues = collectAssignmentValidationIssues(
                    out,
                    activePart.inputs,
                    partsCatalog,
                    mainPartById,
                  );
                  const variantIssues = collectVariantSettingsIssues(
                    out,
                    partsCatalog,
                  );
                  const partOk =
                    !!out.baseKey &&
                    matchIssues.length === 0 &&
                    variantIssues.length === 0;
                  const manualAssignments = out.manualInputAssignments || {};
                  const inputsPg = paginate(configuredInputs, inputsPage);
                  const outputRows = out.baseKey
                    ? selectedVariants.length
                      ? selectedVariants
                      : [String(out.baseKey)]
                    : [];
                  const outputsPg = paginate(outputRows, outputsPage);
                  const settingsPg = paginate(selectedVariants, settingsPage);

                  return (
                    <div
                      className={`pm-view__part pm-view__part--single${partOk ? " pm-view__part--ok" : " pm-view__part--warn"}`}
                    >
                      <div className="pm-view__part-head">
                        <div className="pm-view__part-title-wrap min-w-0">
                          <span className="pm-view__part-num">
                            {activePartIdx + 1}
                          </span>
                          <div className="min-w-0">
                            <div className="pm-view__part-title text-truncate">
                              {title}
                            </div>
                            <div className="pm-view__part-sub">
                              <span className="text-muted">
                                {configuredInputs.length} girdi ·{" "}
                                {selectedVariants.length} varyant
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="pm-view__part-badges">
                          {/* <Lozenge
                            appearance={
                              partOk ? "success-subtle" : "warning-subtle"
                            }
                            isBold
                          >
                            {partOk ? "Hazır" : "Eksik"}
                          </Lozenge>
                          <Lozenge
                            appearance={
                              out.isActive !== false
                                ? "success-subtle"
                                : "default-subtle"
                            }
                          >
                            {out.isActive !== false ? "Aktif" : "Pasif"}
                          </Lozenge> */}
                          {canWrite && (
                            <Button
                              appearance="warning-subtle"
                              icon={<FiEdit2 />}
                              onClick={() =>
                                navigate(`/part-machines/assign/${machineId}`)
                              }
                            >
                              Düzenle
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="pm-view__part-body">
                        {(matchIssues.length > 0 ||
                          variantIssues.length > 0) && (
                          <div className="alert-inline alert-inline--danger mb-3">
                            <ul className="mb-0 ps-3 small">
                              {[...matchIssues, ...variantIssues].map(
                                (issue, i) => (
                                  <li key={i}>{issue.message}</li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}

                        {/* Inputs table */}
                        <div className="pm-view__section">
                          <div className="pm-view__section-head">
                            <FiList size={16} />
                            <strong>Girdiler</strong>
                            <span className="text-muted small">
                              ({configuredInputs.length})
                            </span>
                          </div>
                          <ViewTablePanel
                            total={inputsPg.total}
                            page={inputsPage}
                            onPageChange={setInputsPage}
                          >
                            <table className="pm-view__table">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Girdi</th>
                                  <th>Detay</th>
                                </tr>
                              </thead>
                              <tbody>
                                {inputsPg.items.length ? (
                                  inputsPg.items.map((entry, i) => {
                                    const s = entryInputSummary(
                                      entry,
                                      inputsPg.start + i,
                                      mainPartById,
                                      partsCatalog,
                                    );
                                    return (
                                      <tr key={inputsPg.start + i}>
                                        <td>{inputsPg.start + i + 1}</td>
                                        <td>
                                          <strong>{s.title}</strong>
                                        </td>
                                        <td className="text-muted">
                                          {s.meta}
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td colSpan={3} className="text-muted">
                                      Girdi tanımlı değil
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </ViewTablePanel>
                        </div>

                        {/* Output ↔ girdi assignments */}
                        <div className="pm-view__section">
                          <div className="pm-view__section-head">
                            <FiLink size={16} />
                            <strong>Girdi atamaları</strong>
                            <span className="text-muted small">
                              {selectedVariants.length
                                ? `(${selectedVariants.length} çıktı)`
                                : out.baseKey
                                  ? "(varyantsız)"
                                  : "(seçilmedi)"}
                            </span>
                          </div>
                          {!out.baseKey ? (
                            <p className="text-muted small mb-0">
                              Çıktı ana parça seçilmedi
                            </p>
                          ) : (
                            <ViewTablePanel
                              total={outputsPg.total}
                              page={outputsPage}
                              onPageChange={setOutputsPage}
                            >
                              <table className="pm-view__table">
                                  <thead>
                                    <tr>
                                      <th>#</th>
                                      <th>Çıktı</th>
                                      <th>Atanan girdiler</th>
                                      <th>Durum</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {outputsPg.items.map((vid, i) => {
                                      const doc = partDocById(
                                        vid,
                                        out.variantItems || [],
                                        partsCatalog,
                                      );
                                      const assigned = getOutputAssignmentLabels(
                                        vid,
                                        manualAssignments,
                                        activePart.inputs,
                                        partsCatalog,
                                      );
                                      const assignedOk = assigned.length > 0;
                                      return (
                                        <tr key={vid}>
                                          <td>{outputsPg.start + i + 1}</td>
                                          <td>
                                            {doc ? partInputLabel(doc) : vid}
                                          </td>
                                          <td>
                                            {assignedOk ? (
                                              <ul
                                                className={assignListClass(
                                                  assigned.length,
                                                )}
                                              >
                                                {assigned.map((label, j) => (
                                                  <li key={j}>{label}</li>
                                                ))}
                                              </ul>
                                            ) : (
                                              <span className="text-muted">
                                                Atanmadı
                                              </span>
                                            )}
                                          </td>
                                          <td>
                                            {assignedOk ? (
                                              <Lozenge
                                                appearance="success-subtle"
                                                isBold
                                              >
                                                Atandı
                                              </Lozenge>
                                            ) : (
                                              <Lozenge
                                                appearance="warning-subtle"
                                                isBold
                                              >
                                                Eksik
                                              </Lozenge>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                            </ViewTablePanel>
                          )}
                        </div>

                        {/* Settings table */}
                        <div className="pm-view__section">
                          <div className="pm-view__section-head">
                            <FiClock size={16} />
                            <strong>Varyant ayarları</strong>
                          </div>
                          <ViewTablePanel
                            total={settingsPg.total}
                            page={settingsPage}
                            onPageChange={setSettingsPage}
                          >
                            <table className="pm-view__table">
                              <thead>
                                <tr>
                                  <th>Varyant</th>
                                  <th>Üretim miktarı</th>
                                  <th>Karşılık ağırlığı</th>
                                  <th>Fire</th>
                                  <th>Net çevrim</th>
                                  <th>Eksenler</th>
                                  <th>Nihai ürün</th>
                                </tr>
                              </thead>
                              <tbody>
                                {settingsPg.items.length ? (
                                  settingsPg.items.map((vid) => {
                                      const doc = partDocById(
                                        vid,
                                        out.variantItems || [],
                                        partsCatalog,
                                      );
                                      const settings = getOutputSettings(
                                        out,
                                        vid,
                                      );
                                      const done = isVariantSettingsComplete(
                                        out,
                                        vid,
                                      );
                                      const cycleUnit = partUnitLabelForDoc(
                                        doc,
                                        mainPartById,
                                      );
                                      const weightCalc =
                                        computeOutputEquivalentWeight({
                                          outputPartId: vid,
                                          inputs: activePart.inputs,
                                          manualAssignments:
                                            out.manualInputAssignments || {},
                                          catalog: partsCatalog,
                                          mainPartById,
                                          cycleQuantity: settings.cycleQuantity,
                                          wastage: settings.wastage,
                                        });
                                      const wastageNum = Math.max(
                                        0,
                                        Number(settings.wastage) || 0,
                                      );
                                      const eqUnit = inferEquivalentUnitSymbol({
                                        outputPartId: vid,
                                        inputs: activePart.inputs,
                                        manualAssignments:
                                          out.manualInputAssignments || {},
                                        catalog: partsCatalog,
                                        mainPartById,
                                        weightCalc,
                                      });
                                      return (
                                        <tr key={vid}>
                                          <td>
                                            <span className="d-flex align-items-center gap-2">
                                              {done ? (
                                                <FiCheck
                                                  size={14}
                                                  className="text-success"
                                                />
                                              ) : null}
                                              {doc ? partInputLabel(doc) : vid}
                                            </span>
                                          </td>
                                          <td>
                                            {quantityWithUnit(
                                              settings.cycleQuantity,
                                              cycleUnit,
                                            )}
                                          </td>
                                          <td>
                                            {weightCalc.ok ? (
                                              <MetricAmount
                                                value={weightCalc.net}
                                                unit={eqUnit}
                                              />
                                            ) : (
                                              "—"
                                            )}
                                          </td>
                                          <td>
                                            <MetricAmount
                                              value={wastageNum}
                                              unit={eqUnit}
                                            />
                                          </td>
                                          <td>
                                            {settings.netCycleTime
                                              ? `${settings.netCycleTime} sn`
                                              : "—"}
                                          </td>
                                          <td>
                                            {(() => {
                                              const axes = settings.measurementAxes || [];
                                              const parts = axes
                                                .filter((a) => String(a.payloadKey ?? "").trim() && Number(a.expected) > 0)
                                                .map((a) => `${a.payloadKey}=${a.expected}(${a.tolerance}%)`);
                                              return parts.length > 0 ? parts.join(", ") : "—";
                                            })()}
                                          </td>
                                          <td>
                                            {settings.isFinalProduct
                                              ? modelLabelById(
                                                  settings.deviceModel,
                                                )
                                              : "Hayır"}
                                          </td>
                                        </tr>
                                      );
                                    })
                                  ) : (
                                    <tr>
                                      <td colSpan={9} className="text-muted">
                                        Varyant yok
                                      </td>
                                    </tr>
                                )}
                              </tbody>
                            </table>
                          </ViewTablePanel>
                        </div>
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          )}
        </div>
      </CardContainer>
    </AppPage>
  );
};

export default withAuth(PartMachineAssignmentView);
